import * as React from 'react';
import Card from 'components/Card';
import { calculateCallPutMixFromValues } from 'utils/shared/trades/calculateCallPutMix';
import { formatCount, formatPerContract } from 'utils/shared/number/formatFlowMoney';

interface Props {
  callContracts?: number;
  putContracts?: number;
  callPremium?: number;
  putPremium?: number;
  /**
   * Replaces the plain title, for surfaces that put a control there. The contract total
   * steps aside when one is supplied — both sides are already listed in the table below.
   */
  titleNode?: React.ReactNode;
  variant?: 'default' | 'compact';
  className?: string;
}

// --- dome geometry ---------------------------------------------------------
// Band r 41.5..52.5 -> x [3.5, 108.5]; bloom r 38.5..55.5 -> x [0.5, 111.5], which
// is what sets the 112x58 viewBox. Butt caps end flush on the diameter at y=56, so
// the dome has a true flat base and nothing is clipped. (Round caps would be half
// discs reaching y=61.5 and would be sliced.)
const CX = 56;
const CY = 56;
const R = 47;
// Slimmer band (r 42.5..51.5 -> x [4.5, 107.5]) so the gauge reads as a fine instrument
// rather than a heavy ring.
const STROKE = 9;
// Bloom band r 40.5..53.5 -> x [2.5, 109.5], comfortably inside the 112x58 viewBox. Kept
// narrow and faint so the dome reads as a clean gauge rather than a glowing one — the glow
// was a big part of what made this card feel heavy next to the calmer tables around it.
const BLOOM = 13;
const ARC = 'M 9 56 A 47 47 0 0 1 103 56'; // from 9 o'clock, over the top
const ARC_REVERSED = 'M 103 56 A 47 47 0 0 0 9 56'; // from 3 o'clock, over the top
const ARC_LENGTH = Math.PI * R;
const GAP = 2; // reserved at the boundary -> a clean 4px break between the two arcs
const MIN_ARC = 3; // a side with real volume always draws a visible sliver

const GRAPH_PUT = '#f04662';
const GRAPH_CALL = '#17be64';

interface DomeProps {
  putShare: number;
  putPercentage: number;
  callPercentage: number;
  hasVolume: boolean;
  /** Rendered pixel width. The viewBox is fixed, so the dome scales uniformly. */
  width?: number;
}

const Dome: React.FC<DomeProps> = ({
  putShare,
  putPercentage,
  callPercentage,
  hasVolume,
  width = 112,
}) => {
  const hasSplit = hasVolume && putPercentage > 0 && callPercentage > 0;
  // `calculateSplit` floors a non-zero side at 1%, but the arc is drawn from the raw
  // share, so a lopsided-but-real split gives one side almost no arc to work with:
  // 3 puts against 5,000 calls is 0.09 units, and subtracting a 2-unit gap from that
  // goes negative and drops the sliver while the card still prints "1% PUTS".
  //
  // Clamping the leading side into [MIN_ARC, drawable - MIN_ARC] and giving the other
  // the exact remainder keeps BOTH ends symmetric: the two arcs sum to `drawable` at
  // every value, so the boundary break is 4 units whichever side is thin.
  const rawPutLength = putShare * ARC_LENGTH;
  const drawable = ARC_LENGTH - GAP * 2;
  const putLength = hasSplit
    ? Math.min(Math.max(rawPutLength - GAP, MIN_ARC), drawable - MIN_ARC)
    : rawPutLength;
  const callLength = hasSplit ? drawable - putLength : ARC_LENGTH - rawPutLength;

  // Split angle from +x (0 = 3 o'clock, PI = 9 o'clock); puts grow from 9 o'clock.
  // The clamp is derived from the geometry rather than guessed: at the limit the tick
  // sits in the boundary break, not inside the sliver it is meant to separate.
  const THETA_MIN = (MIN_ARC + GAP) / R;
  const theta = Math.min(Math.max(Math.PI * (1 - putShare), THETA_MIN), Math.PI - THETA_MIN);
  const at = (radius: number) => ({
    x: CX + radius * Math.cos(theta),
    y: CY - radius * Math.sin(theta),
  });
  const inner = at(41);
  const outer = at(53);

  const putsLead = putPercentage > callPercentage;
  const tie = hasVolume && putPercentage === callPercentage;
  const leadPercentage = !hasVolume ? 0 : tie ? 50 : putsLead ? putPercentage : callPercentage;
  const leadWord = !hasVolume ? 'NO FLOW' : tie ? 'SPLIT' : putsLead ? 'PUTS' : 'CALLS';
  const leadFill = !hasVolume ? '#B9BBBE' : tie ? '#B9BBBE' : putsLead ? '#ea7b7b' : '#05EA8C';

  return (
    <svg
      width={width}
      height={Math.round((width * 58) / 112)}
      viewBox="0 0 112 58"
      className="block shrink-0"
      role="img"
    >
      <title>
        {hasVolume
          ? `${putPercentage} percent puts, ${callPercentage} percent calls by contract volume`
          : 'No contract volume in view'}
      </title>
      <path
        d={ARC}
        fill="none"
        stroke="rgba(255,255,255,0.06)"
        strokeWidth={STROKE}
        strokeLinecap="butt"
      />
      {/* Bloom: a wider low-alpha copy of the dominant arc. Cheaper than an SVG blur
          filter (no per-tick raster pass) and it cannot bleed across the boundary and
          muddy the split, because it is the same colour as the arc beneath it. */}
      {hasVolume && !tie && (
        <path
          d={putsLead ? ARC : ARC_REVERSED}
          fill="none"
          stroke={putsLead ? GRAPH_PUT : GRAPH_CALL}
          strokeWidth={BLOOM}
          strokeLinecap="butt"
          opacity={0.1}
          strokeDasharray={`${putsLead ? putLength : callLength} ${ARC_LENGTH}`}
        />
      )}
      {hasVolume && putLength > 0 && (
        <path
          d={ARC}
          fill="none"
          stroke={GRAPH_PUT}
          strokeWidth={STROKE}
          strokeLinecap="butt"
          strokeDasharray={`${putLength} ${ARC_LENGTH}`}
          style={{ transition: 'stroke-dasharray 220ms ease-out' }}
        />
      )}
      {/* Calls run the reversed path so stroke-dashoffset is never negative. */}
      {hasVolume && callLength > 0 && (
        <path
          d={ARC_REVERSED}
          fill="none"
          stroke={GRAPH_CALL}
          strokeWidth={STROKE}
          strokeLinecap="butt"
          strokeDasharray={`${callLength} ${ARC_LENGTH}`}
          style={{ transition: 'stroke-dasharray 220ms ease-out' }}
        />
      )}
      {hasSplit && (
        <line
          x1={inner.x}
          y1={inner.y}
          x2={outer.x}
          y2={outer.y}
          stroke="rgba(255,255,255,0.85)"
          strokeWidth={1.5}
          strokeLinecap="butt"
        />
      )}
      <text
        x={CX}
        y={43}
        textAnchor="middle"
        fontSize={16}
        fontWeight={600}
        className="tabular-nums"
        fill={leadFill}
      >
        {hasVolume ? `${leadPercentage}%` : '—'}
      </text>
      <text
        x={CX}
        y={53}
        textAnchor="middle"
        fontSize={9}
        fontWeight={600}
        letterSpacing="0.14em"
        fill="#B9BBBE"
      >
        {leadWord}
      </text>
    </svg>
  );
};

const SideRow: React.FC<{ label: string; value: string; perContract: string; tone: string }> = ({
  label,
  value,
  perContract,
  tone,
}) => (
  <>
    <div className="flex h-[13px] items-baseline justify-between gap-2">
      <span className="shrink-0 text-[9px] font-semibold uppercase leading-[13px] tracking-[0.12em] text-palette-black-7">
        {label}
      </span>
      <span
        className={`truncate text-[12px] font-semibold tabular-nums leading-[13px] ${tone}`}
        aria-label={`${label}: ${value} contracts`}
      >
        {value}
      </span>
    </div>
    {/* The per-contract ratio is the one figure here that appears nowhere else on the
        row, so it carries its own name rather than relying on position to explain it. */}
    <div
      className="flex h-[10px] items-baseline justify-between gap-1 text-[9px] font-medium leading-[10px] tracking-[0.04em]"
      title={`Average premium per ${label.toLowerCase().replace(/s$/, '')} contract`}
    >
      <span className="shrink-0 text-palette-black-6">AVG/CT</span>
      <span className="truncate tabular-nums text-palette-black-7">{perContract}</span>
    </div>
  </>
);

/**
 * Call vs put share of the visible CONTRACT volume, drawn as a 180-degree dome.
 *
 * Deliberately a different species from the net premium card beside it: a radial
 * instrument read as an angle in contracts, sitting asymmetrically beside a table,
 * against that card's mirror-symmetric linear rail read in dollars.
 *
 * The arc denominator must never become `callPremium + putPremium`. Under the default
 * buy-side filter every call is bullish and every put is bearish, so a premium split
 * here would redraw the net premium card as a circle. Average premium per contract is
 * carried instead — a ratio the other card is arithmetically incapable of containing,
 * and the reason the two cards can differ (70% bearish by premium, 55% puts by
 * contracts) without either being wrong.
 */
const CallPutMixCard: React.FC<React.PropsWithChildren<Props>> = ({
  callContracts = 0,
  putContracts = 0,
  callPremium = 0,
  putPremium = 0,
  titleNode,
  variant = 'default',
  className,
}) => {
  const mix = calculateCallPutMixFromValues(callContracts, putContracts, callPremium, putPremium);
  const hasVolume = mix.totalContracts > 0;
  const compact = variant === 'compact';

  const content = (
    <>
      <div
        className={
          titleNode
            ? 'flex items-center justify-between gap-2'
            : 'flex h-4 items-center justify-between gap-2'
        }
      >
        {titleNode || (
          <span
            className="truncate text-[11px] font-medium leading-4 text-palette-black-7"
            title="Call and put contract volume as a share of the total."
          >
            Call / Put Volume
          </span>
        )}
        {/* The compact sidebar is only 200px wide: the title needs all of it, and
            the totals are already spelled out in the table directly below. */}
        {!compact && !titleNode && (
          <span className="shrink-0 text-[10px] font-medium leading-[11px] tracking-[0.06em] text-palette-black-7">
            {hasVolume ? (
              <>
                <span className="tabular-nums text-palette-black-8">
                  {formatCount(mix.totalContracts)}
                </span>{' '}
                CONTRACTS
              </>
            ) : (
              'NO CONTRACTS'
            )}
          </span>
        )}
      </div>

      <div className={`mt-[5px] flex items-center gap-[10px] ${compact ? 'h-12' : 'h-16'}`}>
        <Dome
          putShare={mix.putShare}
          putPercentage={mix.putPercentage}
          callPercentage={mix.callPercentage}
          hasVolume={hasVolume}
          width={compact ? 84 : 112}
        />
        <div className="flex min-w-0 flex-1 flex-col justify-center">
          <SideRow
            label="Puts"
            value={formatCount(mix.putContracts)}
            perContract={formatPerContract(mix.putPremium, mix.putContracts)}
            tone="text-put-text"
          />
          <div className="my-1 h-px bg-white/10" />
          <SideRow
            label="Calls"
            value={formatCount(mix.callContracts)}
            perContract={formatPerContract(mix.callPremium, mix.callContracts)}
            tone="text-call-text"
          />
        </div>
      </div>
    </>
  );

  if (compact) {
    return (
      <div className={`w-full border-t border-palette-border pb-2 pt-4 ${className || ''}`}>
        {content}
      </div>
    );
  }

  return (
    <Card
      className={`flex w-full flex-col justify-center md:w-1/3 ${className || ''}`}
      style={{ padding: 12 }}
    >
      {content}
    </Card>
  );
};

export default CallPutMixCard;
